import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, Ref } from 'react';
import { Icon } from '../display/Icon.tsx';
import { Tooltip } from '../overlays/Tooltip.tsx';
import styles from './IconButton.module.css';
import { Spinner } from './Spinner.tsx';

export type IconButtonVariant = 'ghost' | 'secondary' | 'primary' | 'danger' | 'muted';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> {
  readonly icon: LucideIcon;
  /** Accessible name and tooltip text. */
  readonly label: string;
  /** Keyboard shortcut shown in the tooltip. */
  readonly shortcut?: string | undefined;
  readonly variant?: IconButtonVariant | undefined;
  readonly size?: IconButtonSize | undefined;
  readonly loading?: boolean | undefined;
  readonly tooltipSide?: 'top' | 'bottom' | 'left' | 'right' | undefined;
  readonly ref?: Ref<HTMLButtonElement> | undefined;
}

/** Icon-only button. Always labelled; the label doubles as its tooltip. */
export const IconButton = ({
  icon,
  label,
  shortcut,
  variant = 'ghost',
  size = 'md',
  loading = false,
  tooltipSide,
  className,
  disabled,
  type = 'button',
  ...rest
}: IconButtonProps) => (
  <Tooltip
    side={tooltipSide}
    content={
      shortcut ? (
        <span className={styles.tip}>
          {label}
          <kbd className={styles.kbd}>{shortcut}</kbd>
        </span>
      ) : (
        label
      )
    }
  >
    <button
      type={type}
      aria-label={label}
      className={clsx(styles.button, styles[variant], styles[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner /> : <Icon icon={icon} size={size === 'lg' ? 'lg' : 'md'} />}
    </button>
  </Tooltip>
);
